import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { merge } from 'lodash-es';
import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { LocalizationOptionService } from './localization-option.service';

export interface TranslationEntry {
  translation: string;
  notes?: string;
  limit?: number;
}

export interface LokaliseResourceSet {
  [key: string]: string | TranslationEntry | LokaliseResourceSet;
}

export function isInstanceOfTranslationEntry(val: string | TranslationEntry | LokaliseResourceSet): val is TranslationEntry {
  return typeof val === 'object' && typeof val.translation === 'string';
}

export interface TranslationSet {
  [key: string]: string | TranslationSet;
}

export class LazyTranslateLoader implements TranslateLoader {
  public constructor(
    private http: HttpClient,
    public prefix: string | string[],
    public suffix: string = '.json',
  ) {}

  public getTranslation(lang: string): Observable<TranslationSet> {
    if (this.prefix instanceof Array) {
      const requests = this.prefix.map((p) =>
        this.http.get<LokaliseResourceSet>(`${p}${lang}${this.suffix}`).pipe(map((resource) => this.transform(resource))),
      );

      return forkJoin(requests).pipe(
        map((results) =>
          results.reduce((acc, curr) => {
            merge(acc, curr);
            return acc;
          }, {}),
        ),
      );
    } else {
      return this.http.get<LokaliseResourceSet>(`${this.prefix}${lang}${this.suffix}`).pipe(map((resource) => this.transform(resource)));
    }
  }

  private transform(lokaliseResource: LokaliseResourceSet, translationSet: TranslationSet = {}): TranslationSet {
    const keys = Object.keys(lokaliseResource);
    let val = null;
    for (const key of keys) {
      val = lokaliseResource[key];
      if (isInstanceOfTranslationEntry(val)) {
        if (val.translation && val.translation.length) {
          translationSet[key] = val.translation;
        } else {
          translationSet[key] = key;
        }
      } else if (typeof val === 'string') {
        translationSet[key] = val;
      } else {
        translationSet[key] = this.transform(val);
      }
    }

    return translationSet;
  }
}

export function httpLoaderFactory(http: HttpClient, optionService: LocalizationOptionService) {
  const options = optionService.options;
  return new LazyTranslateLoader(http, options.folderPrefix, options.fileSuffix);
}
